import { useState, useEffect, useRef } from 'react';
import {
  ChartBarIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { reportService, ReportTemplate, GeneratedReport, GenerateReportRequest } from '../services/reportService';

import LegacyReportExports from './LegacyReportExports';
import './reports.css';

type ReportFormat = 'pdf' | 'xlsx' | 'csv';

export default function ReportsPage() {
  const [preview, setPreview] = useState<{url:string;title:string}|null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewRequest = useRef(0);
  useEffect(() => () => { previewRequest.current++; }, []);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);
  const closePreview = () => {previewRequest.current++;setPreview(null);setPreviewLoading(false);};
  const showPreview = async (report: GeneratedReport) => {
    const request=++previewRequest.current;setPreview(null);setPreviewLoading(true);setError(null);
    try {const blob=await reportService.previewReport(report.id);if(request===previewRequest.current)setPreview({url:URL.createObjectURL(blob),title:report.title});}
    catch(err){if(request===previewRequest.current)setError(err instanceof Error?err.message:'Aperçu indisponible.');}
    finally{if(request===previewRequest.current)setPreviewLoading(false);}
  };
  const generationLock = useRef(false);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [reports, setReports] = useState<GeneratedReport[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0,7)+'-01');
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0,10));
  const [granularity,setGranularity]=useState('month');
  const [filters,setFilters]=useState<Record<string,string>>({});
  const selected=templates.find(t=>t.id===selectedTemplate);
  const profile=String(selected?.config?.report_kind||'');
  const validPeriod=!!startDate&&!!endDate&&endDate>=startDate&&(Date.parse(endDate)-Date.parse(startDate))<=366*86400000;
  const [format, setFormat] = useState<ReportFormat>('pdf');
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'generate' | 'history' | 'exports'>('generate');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [templatesRes, reportsRes] = await Promise.allSettled([
        reportService.getTemplates('hotel'),
        reportService.getReports(),
      ]);
      if (templatesRes.status === 'fulfilled') {
        setTemplates(templatesRes.value || []);
      } else {
        console.warn('Failed to load report templates:', templatesRes.reason);
        setTemplates([]);setError(`Les modèles de rapport sont indisponibles. ${templatesRes.reason instanceof Error ? templatesRes.reason.message : 'Réessayez.'}`);
      }
      if (reportsRes.status === 'fulfilled') {
        setReports(reportsRes.value || []);
      } else {
        console.warn('Failed to load reports:', reportsRes.reason);
        setReports([]);setError(`L’historique des rapports est indisponible. ${reportsRes.reason instanceof Error ? reportsRes.reason.message : 'Réessayez.'}`);
      }
    } catch (err) {
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedTemplate || !validPeriod || generationLock.current) return;
    const template = templates.find(t => t.id === selectedTemplate);
    if (!template) return;

    try {
      generationLock.current = true;
      setGenerating(true);
      setError(null);
      const req: GenerateReportRequest = {
        template_id: selectedTemplate,
        title: `${template.name} - ${new Date().toLocaleDateString('fr-FR')}`,
        category: template.category,
        format,
        period_start: startDate,
        period_end: endDate,
        parameters: {granularity,...filters},
      };
      await reportService.generateReport(req);
      await loadData();
      setActiveTab('history');
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la génération du rapport');
    } finally {
      generationLock.current = false;
      setGenerating(false);
    }
  };

  const handleDownload = async (reportId: string) => {
    setError(null);
    try {
      await reportService.downloadReport(reportId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du téléchargement');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" /> Terminé
          </span>
        );
      case 'generating':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" /> En cours
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="w-3 h-3 mr-1" /> Échoué
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <ClockIcon className="w-3 h-3 mr-1" /> En attente
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="fpt-reports">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Rapports</h1>
              <p className="text-gray-600 mt-1">Générez et consultez les rapports de votre établissement — FPT Assujetti</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p role="alert" className="text-red-700">{error}</p><button className="underline mt-2" onClick={loadData}>Réessayer</button>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('generate')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'generate'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Générer un rapport
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Historique ({reports.length})
            </button>
            <button onClick={() => setActiveTab('exports')} className="py-4 px-1 font-medium text-sm">Exports FPT et licences</button>
            <button onClick={loadData} disabled={generating} className="py-4 px-1 text-sm">Actualiser</button>
          </nav>
        </div>

        {activeTab === 'exports' ? <LegacyReportExports/> : activeTab === 'generate' ? (
          <div className="space-y-6">
            {/* Template Selection */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Choisir un modèle</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.length === 0 && <p role="status">Aucun modèle de rapport disponible.</p>}
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => {setSelectedTemplate(template.id);setFilters({})}}
                    className={`p-4 rounded-lg border-2 text-left transition-colors ${
                      selectedTemplate === template.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <h3 className="font-medium text-gray-900">{template.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                    <span className="inline-block mt-2 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {template.template_type}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Configuration */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Configuration</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <CalendarIcon className="inline h-4 w-4 mr-1" /> Date de début
                  </label>
                  <input
                    aria-label="Date de début" type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <CalendarIcon className="inline h-4 w-4 mr-1" /> Date de fin
                  </label>
                  <input
                    aria-label="Date de fin" type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
                  <select
                    aria-label="Format" value={format}
                    onChange={(e) => setFormat(e.target.value as ReportFormat)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="pdf">PDF</option>
                    <option value="xlsx">Excel (XLSX)</option>
                    <option value="csv">CSV</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <label className="text-sm">Établissement<input readOnly value="Établissement de votre session" className="block w-full border rounded p-2 bg-gray-50"/><span className="text-gray-500">Les données sont limitées à votre établissement autorisé.</span></label>
                <label className="text-sm">Mois ciblé<input aria-label="Mois ciblé" type="month" className="block w-full border rounded p-2" onChange={e=>{if(!e.target.value)return;const [y,m]=e.target.value.split('-').map(Number);setStartDate(e.target.value+'-01');setEndDate(new Date(Date.UTC(y,m,0)).toISOString().slice(0,10))}}/></label>
                <label className="text-sm">Regroupement<select aria-label="Regroupement" value={granularity} onChange={e=>setGranularity(e.target.value)} className="block w-full border rounded p-2"><option value="month">Par mois</option><option value="day">Par jour</option></select></label>
                {['security','police'].includes(profile)&&<>
                  <label className="text-sm">Nationalité<input aria-label="Nationalité" maxLength={100} placeholder="Toutes — ou valeur exacte enregistrée" value={filters.nationality||''} onChange={e=>setFilters(v=>({...v,nationality:e.target.value}))} className="block w-full border rounded p-2"/></label>
                  <label className="text-sm">Sexe<select aria-label="Sexe" value={filters.gender||''} onChange={e=>setFilters(v=>({...v,gender:e.target.value}))} className="block w-full border rounded p-2"><option value="">Tous</option><option value="male">Masculin</option><option value="female">Féminin</option><option value="other">Autre</option></select></label>
                  <label className="text-sm">Document<select aria-label="Document" value={filters.document_type||''} onChange={e=>setFilters(v=>({...v,document_type:e.target.value}))} className="block w-full border rounded p-2"><option value="">Tous</option><option value="passport">Passeport</option><option value="national_id">Carte nationale</option><option value="driver_license">Permis de conduire</option></select></label>
                </>}
                {profile==='financial'&&<>
                  <label className="text-sm">Source<select aria-label="Source" value={filters.source||''} onChange={e=>setFilters(v=>({...v,source:e.target.value}))} className="block w-full border rounded p-2"><option value="">Toutes</option>{['PMS','POS','API','SDC','DIEF'].map(v=><option key={v}>{v}</option>)}</select></label>
                  <label className="text-sm">Activité<select aria-label="Activité" value={filters.transaction_type||''} onChange={e=>setFilters(v=>({...v,transaction_type:e.target.value}))} className="block w-full border rounded p-2"><option value="">Toutes</option><option value="ACCOMMODATION">Hébergement</option><option value="RESTAURANT">Restauration</option><option value="EXTRAS">Extras</option><option value="OTHER">Autre</option></select></label>
                </>}
              </div>
              {!validPeriod&&<p role="alert" className="text-red-700 mt-3">Choisissez une période valide de 367 jours au maximum.</p>}
              <div className="mt-6">
                <button
                  onClick={handleGenerate}
                  disabled={!selectedTemplate || !validPeriod || generating}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generating ? (
                    <>
                      <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" /> Génération en cours...
                    </>
                  ) : (
                    <>
                      <DocumentArrowDownIcon className="h-5 w-5 mr-2" /> Générer le rapport
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* History Tab */
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Format</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reports.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      Aucun rapport généré. Commencez par en créer un.
                    </td>
                  </tr>
                ) : (
                  reports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{report.title}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 capitalize">{report.category}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 uppercase">{report.format}</td>
                      <td className="px-6 py-4">{getStatusBadge(report.status)}{report.status === 'failed' && report.error_message && <p className="text-red-700 text-sm">{report.error_message}</p>}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(report.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4">
                        {report.status === 'completed' && <button type="button" className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-4" onClick={()=>showPreview(report)}>Aperçu</button>}
                        {report.status === 'completed' && (
                          <button
                            onClick={() => handleDownload(report.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Télécharger
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {(preview || previewLoading) && <section role="region" aria-label="Aperçu du rapport" className="p-4"><button type="button" className="text-blue-600 mb-3" onClick={closePreview}>Fermer l’aperçu</button>{previewLoading?<p role="status">Chargement de l’aperçu…</p>:preview&&<><h3>{preview.title}</h3><iframe sandbox="allow-same-origin" title={`Aperçu — ${preview.title}`} src={preview.url} style={{width:'100%',height:'70vh',border:0}} /></>}</section>}
          </div>
        )}
      </div>
    </div>
  );
}
